import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { MdShoppingCart } from "react-icons/md";
import { MenuItem } from "../redux/slices/menuSlice";
import { parsePrice, formatPrice } from "../utils/priceUtils";

interface CartRecommendationsProps {
  title: string; // "Need anything else?" or "You May Also Like"
  cartItems: any[];
  allItems: MenuItem[];
  onAddToCart: (item: MenuItem) => void;
}

const CartRecommendations: React.FC<CartRecommendationsProps> = ({
  title,
  cartItems,
  allItems,
  onAddToCart,
}) => {
  const recommendedItems = useMemo((): MenuItem[] => {
    if (!allItems?.length || !cartItems?.length) return [];

    // Collect ALL pairings from ALL cart items
    const allPairings = cartItems
      .flatMap(cartItem => cartItem.item?.pairings || []);

    if (allPairings.length === 0) return [];

    return allItems.filter((item) => {
      // Match ANY pairing from cart
      const matchesPairing = allPairings.some((pairing: string) =>
        item.name.toLowerCase().includes(pairing.toLowerCase().trim())
      );
      
      // NOT already in cart
      const isNotInCart = !cartItems.some(cartItem =>
        cartItem.item.pk_id === item.pk_id ||
        cartItem.item.id === item.id
      );
      
      return matchesPairing && isNotInCart;
    });
  }, [allItems, cartItems]);

  if (recommendedItems.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-4"
    >
      <h3 className="text-lg font-semibold mb-3">
        {title}
      </h3>

      {/* Horizontal scroll */}
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        {recommendedItems.map((item) => (
          <motion.div
            key={item.id}
            whileTap={{ scale: 0.97 }}
            className="flex-shrink-0 w-32"
          >
            <RecommendedItemCard
              item={item}
              onAddToCart={() => onAddToCart(item)}
            />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

interface RecommendedItemCardProps {
  item: MenuItem;
  onAddToCart: () => void;
}

const RecommendedItemCard: React.FC<RecommendedItemCardProps> = React.memo(
  ({ item, onAddToCart }) => {
    const renderImage = () => {
      if (item.image) {
        return (
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-20 object-cover rounded-md mb-2"
          />
        );
      }

      return (
        <div className="w-full h-20 bg-red-100 flex items-center justify-center rounded-md mb-2">
          <span className="text-lg font-bold text-red-500">
            {item.name?.[0]?.toUpperCase() || "P"}
          </span>
        </div>
      );
    };

    return (
      <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        <div className="p-2">
          {renderImage()}

          <div>
            <h4 className="font-medium text-xs leading-tight mb-1 line-clamp-1">
              {item.name}
            </h4>
            <div className="flex items-center justify-between">
              <p className="text-red-500 text-xs font-semibold">
                {formatPrice(parsePrice(item.price))}
              </p>
              <button
                onClick={onAddToCart}
                className="hover:scale-110 transition-transform"
                aria-label={`Add ${item.name} to cart`}
              >
                <MdShoppingCart className="w-4 h-4 text-red-500" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

RecommendedItemCard.displayName = "RecommendedItemCard";

export default CartRecommendations;
